// DS — JEDEN odtwarzacz EDIMI (sesja 09, artboardy 09 i 10; G5-edits-06, -08, -22,
// G8-assets-motion-11; sesja 10, artboardy 04–05: wariant źródła AUDIO nad stillem).
//
// Do 2026-09-07 ten plik był dwuwierszowym aliasem `components/Player` — pętli karaoke
// z martwymi przyciskami przewijania i kółkiem-awatarem. Produkt miał trzy odtwarzacze:
// natywny `<video controls>` na stronie editu, karaoke w toku renderu i surowy
// `<input type="range">` w podglądzie serii. Zostaje jeden, ten.
//
// Dwa ŹRÓDŁA, jedna definicja (rozstrzygnięcie 3 briefu 10):
//   · `src` — plik MP4: `<video>` bez `controls` nad plakatem, transport steruje elementem;
//   · `playhead` + `poster` — still (np. styl tekstu) i zegar z zewnątrz (`useAudioPlayhead`
//     w edytorze tekstu): ten sam transport, `<audio>` żyje w hooku wywołującego.
//     Bez pełnego ekranu (still nie ma czego powiększać) — `PlayerTransport` rysuje
//     przycisk tylko, gdy dostał `onFullscreen`.
//
// Reguły:
//   · transport rysuje się WYŁĄCZNIE dla istniejącego pliku — komponent przyjmuje `src`
//     i nie ma stanu „jeszcze nie ma czego grać" (to `RenderSurface` / `StatePanel`);
//   · kadr = `Media ratio="9:16"` (plakat z `thumb_url`), nad nim `<video>` BEZ `controls`;
//   · ścieżka czasu i głośność to `role="slider"` z klawiaturą (strzałki, Home, End),
//     nie natywny suwak — `ds-native-controls.test.ts` nie skanuje `ds/`, więc pilnuje
//     tego `Player.test.tsx`;
//   · bez przycisków przewijania ±5 s (MAPA 09, decyzja 2) — ścieżka niesie tę funkcję;
//   · jeden format czasu m:ss (bieżący --stage-fg, całkowity --stage-fg-dim);
//   · pigułka „gra" z pierścieniem `live` tylko, gdy naprawdę gra;
//   · etykiety od wywołującego (DS nie wysyła polskiego zdania do każdego języka);
//   · czasy ruchu i kolory wyłącznie z tokenów (ds.css, sekcja 09).

import type { KeyboardEvent, PointerEvent, ReactNode } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import Icon from './Icon'
import { Media } from './Media'
import { StatusPill } from '../primitives/StatusPill'

export interface PlayerLabels {
  play: string
  pause: string
  /** Nazwa dostępna ścieżki czasu. */
  seek: string
  volume: string
  mute: string
  unmute: string
  fullscreen: string
  /** Pigułka na kadrze, gdy gra. */
  playing: string
  /** Kadr zanim plakat / pierwsza klatka dojdą (bez plakatu). */
  loading: string
}

/** Zegar z zewnątrz (wariant audio): stan i sterowanie, które transport pokazuje 1:1. */
export interface PlayheadControl {
  t: number
  duration: number
  playing: boolean
  toggle: () => void
  seek: (sec: number) => void
  volume: number
  setVolume: (v: number) => void
  muted: boolean
  toggleMute: () => void
  /** Bez pliku audio: transport wyłączony (oś działa, odtwarzanie nie). */
  disabled?: boolean
}

type Common = {
  labels: PlayerLabels
  /** Elementy nad kadrem (np. numer editu, tekst zsynchronizowany). */
  children?: ReactNode
  className?: string
  'data-testid'?: string
}

export type PlayerVideoProps = Common & {
  /** Podpisany adres MP4 (krótkotrwały). Bez niego odtwarzacza nie ma — rysuj stan. */
  src: string
  /** Plakat: `thumb_url` editu. */
  poster?: string | null
  posterSrcSet?: undefined
  autoPlay?: boolean
  /** Długość z backendu (`duration_sec`) — pokazywana, dopóki metadane pliku nie dojdą. */
  durationHint?: number | null
  onError?: () => void
  playhead?: undefined
}

export type PlayerAudioProps = Common & {
  src?: undefined
  /** Still (styl tekstu) — kadr, nad którym stoi transport. */
  poster: string
  /** Gęstość 2× stilla. */
  posterSrcSet?: string
  playhead: PlayheadControl
  autoPlay?: undefined
  durationHint?: undefined
  onError?: undefined
}

export type PlayerProps = PlayerVideoProps | PlayerAudioProps

/** Sekundy → m:ss. Własne, żeby `ds/` nie sięgało do `lib/`. */
export function clock(s: number): string {
  const n = Number.isFinite(s) && s > 0 ? Math.floor(s) : 0
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`
}

const SEEK_STEP_S = 5
const VOLUME_STEP = 0.1

/** Ułamek 0..1 z pozycji wskaźnika na elemencie. */
function fraction(el: HTMLElement, clientX: number): number {
  const r = el.getBoundingClientRect()
  if (r.width <= 0) return 0
  return Math.max(0, Math.min(1, (clientX - r.left) / r.width))
}

export interface PlayerTransportProps {
  labels: PlayerLabels
  playing: boolean
  time: number
  duration: number
  volume: number
  muted: boolean
  onToggle: () => void
  onSeek: (sec: number) => void
  onVolume: (v: number) => void
  onMute: () => void
  /** Pełny ekran tylko dla wideo — bez tego przycisku transport go nie rysuje. */
  onFullscreen?: () => void
  /** Przeciąganie ścieżki w toku (żeby `timeupdate` nie walczył z palcem). */
  draggingRef?: { current: boolean }
  disabled?: boolean
  className?: string
}

/** Transport odtwarzacza: pauza · m:ss / m:ss · ścieżka `role="slider"` · głośność · [pełny ekran]. */
export function PlayerTransport({
  labels, playing, time, duration, volume, muted, onToggle, onSeek, onVolume, onMute, onFullscreen, draggingRef, disabled, className = '',
}: PlayerTransportProps) {
  const localDrag = useRef(false)
  const dragging = draggingRef ?? localDrag
  const shownVolume = muted ? 0 : volume
  const frac = duration > 0 ? Math.min(1, time / duration) : 0
  const timeText = `${clock(time)} / ${clock(duration)}`

  const onSeekKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return
    const step = { ArrowRight: SEEK_STEP_S, ArrowUp: SEEK_STEP_S, ArrowLeft: -SEEK_STEP_S, ArrowDown: -SEEK_STEP_S }[e.key]
    if (step !== undefined) { e.preventDefault(); onSeek(Math.max(0, Math.min(duration, time + step))); return }
    if (e.key === 'Home') { e.preventDefault(); onSeek(0) }
    else if (e.key === 'End') { e.preventDefault(); onSeek(duration) }
    else if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); onToggle() }
  }
  const onSeekPointer = (e: PointerEvent<HTMLDivElement>) => {
    if (disabled) return
    const el = e.currentTarget
    dragging.current = true
    el.setPointerCapture?.(e.pointerId)
    onSeek(fraction(el, e.clientX) * duration)
  }
  const onSeekMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return
    onSeek(fraction(e.currentTarget, e.clientX) * duration)
  }
  const onSeekUp = (e: PointerEvent<HTMLDivElement>) => {
    dragging.current = false
    e.currentTarget.releasePointerCapture?.(e.pointerId)
  }
  const onVolKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return
    const step = { ArrowRight: VOLUME_STEP, ArrowUp: VOLUME_STEP, ArrowLeft: -VOLUME_STEP, ArrowDown: -VOLUME_STEP }[e.key]
    if (step !== undefined) { e.preventDefault(); onVolume(shownVolume + step); return }
    if (e.key === 'Home') { e.preventDefault(); onVolume(0) }
    else if (e.key === 'End') { e.preventDefault(); onVolume(1) }
  }

  return (
    <div className={['player-tr', disabled && 'is-disabled', className].filter(Boolean).join(' ')}>
      <button type="button" className="player-pb" onClick={onToggle} disabled={disabled} aria-label={playing ? labels.pause : labels.play} aria-pressed={playing}>
        <Icon name={playing ? 'Pause' : 'Play'} size={18} strokeWidth={2} />
      </button>
      <span className="player-tm" aria-hidden="true"><b>{clock(time)}</b> / <span>{clock(duration)}</span></span>
      <div
        className="player-trk"
        role="slider"
        tabIndex={disabled ? -1 : 0}
        aria-label={labels.seek}
        aria-disabled={disabled || undefined}
        aria-valuemin={0}
        aria-valuemax={Math.round(duration)}
        aria-valuenow={Math.round(time)}
        aria-valuetext={timeText}
        onKeyDown={onSeekKey}
        onPointerDown={onSeekPointer}
        onPointerMove={onSeekMove}
        onPointerUp={onSeekUp}
        onPointerCancel={onSeekUp}
      >
        <i style={{ width: `${frac * 100}%` }} />
        <b style={{ left: `${frac * 100}%` }} />
      </div>
      <button type="button" className="player-mute" onClick={onMute} disabled={disabled} aria-label={muted ? labels.unmute : labels.mute} aria-pressed={muted}>
        <Icon name={shownVolume === 0 ? 'VolumeX' : shownVolume < 0.5 ? 'Volume1' : 'Volume2'} size={18} />
      </button>
      <div
        className="player-vol"
        role="slider"
        tabIndex={disabled ? -1 : 0}
        aria-label={labels.volume}
        aria-disabled={disabled || undefined}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(shownVolume * 100)}
        onKeyDown={onVolKey}
        onPointerDown={(e) => { if (!disabled) onVolume(fraction(e.currentTarget, e.clientX)) }}
      >
        <i style={{ width: `${shownVolume * 100}%` }} />
        <b style={{ left: `${shownVolume * 100}%` }} />
      </div>
      {onFullscreen ? (
        <button type="button" className="player-fs" onClick={onFullscreen} disabled={disabled} aria-label={labels.fullscreen}>
          <Icon name="Maximize" size={18} />
        </button>
      ) : null}
    </div>
  )
}

function VideoPlayer({ src, poster, labels, autoPlay, durationHint, onError, children, className = '', ...rest }: PlayerVideoProps) {
  const video = useRef<HTMLVideoElement>(null)
  const [playing, setPlaying] = useState(false)
  const [time, setTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [volume, setVolume] = useState(1)
  const [muted, setMuted] = useState(false)
  const dragging = useRef(false)

  useEffect(() => { setPlaying(false); setTime(0); setDuration(0) }, [src])

  const toggle = useCallback(() => {
    const v = video.current
    if (!v) return
    if (v.paused) void v.play().catch(() => onError?.())
    else v.pause()
  }, [onError])

  const seekTo = useCallback((s: number) => {
    const v = video.current
    if (!v) return
    const max = Number.isFinite(v.duration) && v.duration > 0 ? v.duration : duration
    const next = Math.max(0, Math.min(max || 0, s))
    v.currentTime = next
    setTime(next)
  }, [duration])

  const setVol = useCallback((x: number) => {
    const v = video.current
    const next = Math.max(0, Math.min(1, x))
    setVolume(next)
    if (v) { v.volume = next; v.muted = next === 0 }
    setMuted(next === 0)
  }, [])

  const toggleMute = useCallback(() => {
    const v = video.current
    const next = !muted
    setMuted(next)
    if (v) v.muted = next
  }, [muted])

  const fullscreen = useCallback(() => {
    const v = video.current as (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | null
    if (!v) return
    if (v.requestFullscreen) void v.requestFullscreen().catch(() => {})
    else v.webkitEnterFullscreen?.()
  }, [])

  const shownDuration = duration > 0 ? duration : (durationHint ?? 0)

  const frame = (
    <video
      ref={video}
      src={src}
      poster={poster ?? undefined}
      playsInline
      preload="metadata"
      autoPlay={autoPlay}
      onLoadedMetadata={() => { const v = video.current; if (v) setDuration(Number.isFinite(v.duration) ? v.duration : 0) }}
      onDurationChange={() => { const v = video.current; if (v && Number.isFinite(v.duration)) setDuration(v.duration) }}
      onTimeUpdate={() => { if (!dragging.current) setTime(video.current?.currentTime ?? 0) }}
      onPlay={() => setPlaying(true)}
      onPause={() => setPlaying(false)}
      onEnded={() => setPlaying(false)}
      onVolumeChange={() => { const v = video.current; if (v) { setMuted(v.muted); setVolume(v.volume) } }}
      onError={() => onError?.()}
      onClick={toggle}
    />
  )

  return (
    <div className={['player', playing && 'is-playing', className].filter(Boolean).join(' ')} data-testid={rest['data-testid']}>
      {poster ? (
        <Media ratio="9:16" src={poster} alt="" className="player-stage">
          {frame}
          {playing ? <StatusPill tone="ok" live className="player-live">{labels.playing}</StatusPill> : null}
          {children}
        </Media>
      ) : (
        <Media ratio="9:16" state={{ title: labels.loading }} className="player-stage">
          {frame}
          {playing ? <StatusPill tone="ok" live className="player-live">{labels.playing}</StatusPill> : null}
          {children}
        </Media>
      )}
      <PlayerTransport
        labels={labels}
        playing={playing}
        time={time}
        duration={shownDuration}
        volume={volume}
        muted={muted}
        onToggle={toggle}
        onSeek={seekTo}
        onVolume={setVol}
        onMute={toggleMute}
        onFullscreen={fullscreen}
        draggingRef={dragging}
      />
    </div>
  )
}

function AudioPlayer({ poster, posterSrcSet, playhead, labels, children, className = '', ...rest }: PlayerAudioProps) {
  return (
    <div className={['player', 'player-audio', playhead.playing && 'is-playing', className].filter(Boolean).join(' ')} data-testid={rest['data-testid']}>
      <Media ratio="9:16" src={poster} srcSet={posterSrcSet} alt="" className="player-stage">
        {playhead.playing ? <StatusPill tone="ok" live className="player-live">{labels.playing}</StatusPill> : null}
        {children}
      </Media>
      <PlayerTransport
        labels={labels}
        playing={playhead.playing}
        time={playhead.t}
        duration={playhead.duration}
        volume={playhead.volume}
        muted={playhead.muted}
        onToggle={playhead.toggle}
        onSeek={playhead.seek}
        onVolume={playhead.setVolume}
        onMute={playhead.toggleMute}
        disabled={playhead.disabled}
      />
    </div>
  )
}

export function Player(props: PlayerProps) {
  if ('playhead' in props && props.playhead) return <AudioPlayer {...props} />
  return <VideoPlayer {...(props as PlayerVideoProps)} />
}

export default Player
